import { useNavigate, useLocation } from "react-router-dom";
import { Package, BarChart3, Archive, Settings, ShoppingBag, HelpCircle, Users } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useUserFeatures } from "@/hooks/useUserFeatures";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { getPreloadKey, preloadRoute } from "@/lib/routePreload";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { MobileNavContent } from "@/components/MobileNav";
import { requestFCMToken } from "@/firebase";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useCallback } from "react";


interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * AppSidebar - Navigation sidebar without internal header
 * 
 * The toggle + logo now live in AppNavigation's header-left column.
 * This sidebar starts directly with nav items.
 * 
 * On mobile: renders MobileNavContent inside the Sheet (hamburger drawer)
 * On desktop: renders the persistent sidebar with icons
 */
export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { data: userFeatures } = useUserFeatures();
  const { setShowOnboarding } = useOnboarding();
  const isMobile = useIsMobile();

  // Check feature flags
  const externalIntakeEnabled = userFeatures?.external_order_intake ?? false;
  const customersModuleEnabled = userFeatures?.customers_module ?? false;

  // All nav items in a single flat list (no section headings per Optimo spec)
  const navItems: NavItem[] = [
    {
      title: t("nav.orders"),
      url: "/orders",
      icon: Package,
    },
    // Only show Products if external intake is enabled
    ...(externalIntakeEnabled ? [{
      title: t("nav.products"),
      url: "/products",
      icon: ShoppingBag,
    }] : []),
    // Only show Customers if module is enabled (after Products)
    ...(customersModuleEnabled ? [{
      title: t("nav.customers"),
      url: "/customers",
      icon: Users,
    }] : []),
    {
      title: t("nav.dashboard"),
      url: "/dashboard",
      icon: BarChart3,
    },
    {
      title: t("nav.archive"),
      url: "/archive",
      icon: Archive,
    },
    {
      title: t("nav.configuration"),
      url: "/settings",
      icon: Settings,
    },
  ];
  
  // Handle Help click - opens onboarding modal via context
  const handleHelpClick = useCallback(() => {
    setShowOnboarding(true);
  }, [setShowOnboarding]);
  
  
  useEffect(()=>{
    //enable notifications for push notifications
  async function enableNotifications() {
      //get the user id from supabase
    const { data: { session } } = await supabase.auth.getSession();
    const token = await requestFCMToken();
    if (!token) return;
  
    await supabase
    .from("device_tokens")
    .upsert({ user_id: session?.user?.id, token });
  }

  enableNotifications()

  },[])

  const isActive = (url: string) => location.pathname === url;

  // Preload route on hover for instant navigation
  const handleMouseEnter = useCallback((url: string) => {
    const key = getPreloadKey(url);
    if (key) {
      preloadRoute(key);
    }
  }, []);

  return (
    <Sidebar collapsible="icon">
      {/* Mobile: render MobileNavContent for the hamburger drawer */}
      {isMobile ? (
        <MobileNavContent />
      ) : (
        <>
          {/* No SidebarHeader - toggle + logo are in the header now */}
          <SidebarContent className=" bg-muted pt-1.5">
            {/* Single flat nav group - no headings, no dividers */}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                {navItems.map((item) => {
                    const active = isActive(item.url);
                    return (
                      <SidebarMenuItem key={item.url}>
                        <SidebarMenuButton
                          onClick={() => navigate(item.url)}
                          onMouseEnter={() => handleMouseEnter(item.url)}
                          isActive={active}
                          size="lg"
                          className={cn(
                            // Minimalist: text color shift for active, subtle opacity for inactive
                            "text-muted-foreground transition-colors",
                            !active && "opacity-80 hover:opacity-100 hover:text-foreground hover:bg-primary/[0.06]",
                            active && "text-foreground font-medium opacity-100 "
                          )}
                          aria-current={active ? "page" : undefined}
                        >
                          <item.icon className={cn(
                            "h-4 w-4 transition-colors shrink-0",
                            active && "text-primary"
                          )} />
                          <span className="group-data-[collapsible=icon]:hidden">{item.title}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                  
                  {/* Help / Guide - separate from nav items, always visible */}
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleHelpClick}
                      size="lg"
                      className="text-muted-foreground transition-colors opacity-80 hover:opacity-100 hover:text-foreground hover:bg-primary/[0.06]"
                    >
                      <HelpCircle className="h-4 w-4 transition-colors shrink-0" />
                      <span className="group-data-[collapsible=icon]:hidden">{t("nav.showGuide")}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </>
      )}
    </Sidebar>
  );
}
